<template>
  <v-alert
    v-if="errorMsg"
    class="error-msg"
    :text="errorMsg"
    type="error"
    variant="outlined"
    :closable="true"></v-alert>
  <div class="d-flex flex-column align-center mt-8 intake-detail">
    <v-container :fluid="true" class="lh--container-wide">
      <v-row no-gutters>
        <v-col cols="12">
          <v-alert
            v-if="intakeStore.alertMessage"
            :text="intakeStore.alertMessage?.text"
            :type="intakeStore.alertMessage?.type"
            variant="outlined"></v-alert>
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col cols="12" class="patient-intake-container">
          <v-row no-gutters>
            <v-col cols="12" class="review-container">
              <!-- Adding a :key forces rerendering -->
              <ReadOnlyBanner v-if="isReadOnlyDevice" />
              <ReadOnlyBanner v-if="intakeStore.lockViewer" icon="mdi-account-lock-outline" :text="lockBannerText">
                <template v-if="lockFree && !isReadOnlyDevice" #action>
                  <v-btn size="small" color="primary" variant="flat" @click="takeOverEditing">Take over editing</v-btn>
                </template>
              </ReadOnlyBanner>
              <v-row no-gutters>
                <v-col v-if="!showLoader">
                  <MultiPatientBar
                    v-if="multiPatientChartEnabled"
                    :patient="patientInfo"
                    :additional-patients="additionalPatients"
                    :patient-guess="documentInfo.patientGuess"
                    :client-company="documentInfo.clientCompany"
                    :editable="!intakeStore.isReadOnly && !isReadOnlyDevice"
                    :allow-new-patient-registration="allowNewPatientRegistration"
                    :confirm-selection-loading="confirmSelectionLoading"
                    @set-patient="onSetPatient"
                    @save-new-patient="onSaveNewPatient"
                    @set-additional-patients="onSetAdditionalPatients"
                    @confirm-selection="onConfirmPatientSelection"
                    @update-patient="onUpdatePatient"
                    @highlight-bounding-box="onPatientGuessHighlight" />
                  <PatientBar
                    v-else
                    :patient="patientInfo"
                    :additional-patients="additionalPatients"
                    :patient-guess="documentInfo.patientGuess"
                    :client-company="documentInfo.clientCompany"
                    :editable="!intakeStore.isReadOnly && !isReadOnlyDevice"
                    @change-attribute="onPatientAttributeChange"
                    @set-patient="onSetPatient"
                    @save-new-patient="onSaveNewPatient"
                    @set-additional-patients="onSetAdditionalPatients"
                    @highlight-bounding-box="onPatientGuessHighlight" />
                </v-col>
              </v-row>
              <v-row no-gutters>
                <v-col v-if="!showLoader" cols="12">
                  <ClassificationBar
                    :editable="!intakeStore.isReadOnly && !isReadOnlyDevice"
                    :show-split-button="!isReadOnlyDevice && !intakeStore.isReadOnly" />
                </v-col>
                <v-container v-if="showLoader">
                  <v-skeleton-loader type="text@4" />
                </v-container>
              </v-row>
              <v-row v-if="intakeWarnings.length" no-gutters class="mb-2">
                <v-col v-for="(warning, index) in intakeWarnings" :key="`intake-warning-${index}`" cols="12">
                  <v-alert
                    class="intake-warning"
                    :text="warning.message"
                    :type="warning.type"
                    variant="tonal"
                    density="compact">
                  </v-alert>
                </v-col>
              </v-row>
              <PagePreview
                v-if="!documentSectionLoading && currentDocument && requirementsStore.clientConfig"
                ref="previewRef"
                :patient="patientInfo"
                :order-form="documentInfo.intakeOrderForm"
                :insurance-cards="documentInfo.intakeInsuranceCards"
                :to-dos="documentInfo.intakeToDos"
                @attribute-edit="onAttributeEdit"
                @add-content="onAddContent"
                @delete-content="onDeleteContent"
                @snooze="onSnooze"
                @archive="onArchive" />
              <v-container v-if="documentSectionLoading">
                <v-skeleton-loader type="image@12" />
              </v-container>
              <BottomBar
                v-if="!isReadOnlyDevice"
                :finish-review-enabled="finishReviewEnabled"
                intake-status="Intake"
                @submit-step="submitStep"
                @back-step="backStep">
                <div class="intake-actionbar">
                  <v-btn variant="outlined" color="primary" rounded prepend-icon="mdi-arrow-left" @click="backStep">
                    Go Back
                  </v-btn>
                  <v-btn
                    variant="tonal"
                    color="primary"
                    rounded
                    icon="mdi-download"
                    aria-label="Download document"
                    @click="downloadDocument" />
                  <div class="intake-actionbar__spacer" />
                  <template v-if="!showLoader && !intakeStore.isReadOnly">
                    <v-btn
                      v-for="action in submitActions"
                      :key="action.label"
                      color="primary"
                      variant="flat"
                      rounded
                      append-icon="mdi-check"
                      :disabled="buttonsDisabled"
                      @click="submitStep(action.payload)">
                      {{ action.label }}
                    </v-btn>
                  </template>
                </div>
              </BottomBar>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <v-row v-if="documentInfo && !isReadOnlyDevice && !intakeStore.lockViewer">
        <v-col cols="12">
          <!-- Send Method Toggle (only shown when email is enabled) -->
          <v-btn-toggle
            v-if="sendEmailEnabled"
            v-model="sendMethod"
            mandatory
            class="mb-4 intake-send-toggle"
            color="primary"
            variant="text">
            <v-btn value="fax">
              <v-icon start>mdi-fax</v-icon>
              Send Fax
            </v-btn>
            <v-btn value="email">
              <v-icon start>mdi-email</v-icon>
              Send Email
            </v-btn>
          </v-btn-toggle>

          <OutgoingFaxEditor v-if="sendMethod === 'fax'" :intake-document="documentInfo" title="Send Fax" />
          <OutgoingEmailEditor
            v-if="sendMethod === 'email'"
            :intake-document="documentInfo"
            :to-emails="referralSourceEmails"
            :default-subject="emailSubject"
            :template-context="emailTemplateContext"
            :suggested-files="emailSuggestedFiles"
            title="Send Email" />
        </v-col>
      </v-row>
      <v-row v-if="documentInfo">
        <v-col cols="12">
          <IntakeTagBar
            :tags="documentInfo.statusTags"
            :tag-defs="availableTags"
            :tag-due-dates="documentInfo.tagDueDates"
            entity-type="document"
            :entity-id="documentKey"
            @update:tags="(t) => (documentInfo.statusTags = t)"
            @saved="auditLogListRef?.showLatest()"
            @update:tag-due-dates="(d) => (documentInfo.tagDueDates = d)" />
          <AssigneeBar
            :assignees="documentInfo.assignees || []"
            entity-type="document"
            :entity-id="documentKey"
            @update:assignees="(a) => (documentInfo.assignees = a)" />
          <GroupBar
            :assigned-groups="documentInfo.assignedGroups || []"
            entity-type="document"
            :entity-id="documentKey"
            @update:assigned-groups="(g) => (documentInfo.assignedGroups = g)" />
          <SubstatusBar entity-type="document" :entity-id="documentKey" />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <AuditLogList
            ref="auditLogListRef"
            :intake-document-id="documentKey"
            :tag-defs="availableTags"
            @warning-note="onWarningNote"
            @mentions-sent="onMentionsSent" />
        </v-col>
      </v-row>
    </v-container>
    <v-dialog v-model="archiveReasonDialog" max-width="800">
      <ArchiveNote :intake-document="documentInfo" @on-archived="onArchived" />
    </v-dialog>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref, watch, computed } from "vue";
import { onBeforeRouteLeave, useRoute } from "vue-router";
import { listReturnTarget } from "@/composables/useListReturn";
import { intakeService } from "@/services/intakeService";
import { useRequirementsStore } from "@/stores/requirementsStore";
import PagePreview from "@/components/intakeReview/PagePreview.vue";
import PatientBar from "@/components/intakeReview/PatientBar.vue";
import MultiPatientBar from "@/components/intakeReview/MultiPatientBar.vue";
import BottomBar from "@/components/intakeReview/BottomBar.vue";
import {
  IntakeDocumentPage,
  Patient,
  IntakeDocument,
  INTAKE_STAGE,
  DOCUMENT_STAGE,
  setIntakeSaveErrorHandler,
  clearIntakeSaveErrorHandler,
} from "@/models/intake";
import router from "@/router";
import { patientService } from "@/services/patientService";
import { useDate } from "vuetify";
import { useIntakeStore } from "@/stores/intakeStore";
import ClassificationBar from "@/components/intakeReview/ClassificationBar.vue";
import AuditLogList from "@/components/AuditLogList.vue";
import { getClientIntakeUI } from "@/clientSpecific/base/intakeUI";
import { handleFormValidationErrors } from "@/utils/validators";
import { replaceTableMarkers } from "@/utils/pastedTable";
import { useIntakeActions } from "@/composables/useIntakeActions";
import { useIntakeListFiltersStore } from "@/stores/intakeListFiltersStore";
import ArchiveNote from "@/components/ArchiveNote.vue";
import OutgoingFaxEditor from "@/components/OutgoingFaxEditor.vue";
import OutgoingEmailEditor from "@/components/OutgoingEmailEditor.vue";
import IntakeTagBar from "@/components/dashboard/IntakeTagBar.vue";
import AssigneeBar from "@/components/dashboard/AssigneeBar.vue";
import GroupBar from "@/components/dashboard/GroupBar.vue";
import SubstatusBar from "@/components/dashboard/SubstatusBar.vue";
import ReadOnlyBanner from "@/components/dashboard/ReadOnlyBanner.vue";
import { useResponsive } from "@/composables/useResponsive";
import { useIntervalWhenVisible } from "@/composables/useIntervalWhenVisible";
import { convertDateToMMDDYYYY, formatLockHolderName } from "@/utils";

const route = useRoute();
const dateAdapter = useDate();
const { isReadOnlyDevice } = useResponsive();

const intakeStore = useIntakeStore();
const requirementsStore = useRequirementsStore();
const intakeActions = useIntakeActions();
const filtersStore = useIntakeListFiltersStore();

const documentInfo = computed(() => intakeStore.intakeDocument);
const multiPatientChartEnabled = computed(
  () => requirementsStore.clientConfig?.intake?.multiPatientChartEnabled ?? false
);
const patientInfo = ref(null);
const additionalPatients = ref([]);
const confirmSelectionLoading = ref(false);
const allPages = ref([]);
const reviewContainerHeight = ref("70vh");
const currentDocument = ref(null);
const finishReviewEnabled = ref(false);
const pageManager = intakeStore.pageManager;
const errorMsg = ref("");
const documentKey = route.params.id;
const previewRef = ref(null);
const showLoader = ref(true);
const pagesLoading = ref(true);
const clientConfigLoading = ref(true);
const documentSectionLoading = computed(() => showLoader.value || pagesLoading.value || clientConfigLoading.value);
const buttonsDisabled = ref(false);
const auditLogListRef = ref(null);
let pollInterval = null;
const holdsLock = ref(false);
const lockHolder = ref(null);
const lockFree = ref(false);
const wasEditor = ref(false);

const lockBannerText = computed(() => {
  if (lockFree.value) return "The record is free — you can take over editing.";
  const name = formatLockHolderName(lockHolder.value);
  return name ? `${name} is editing this record — view only.` : "Another user is editing this record — view only.";
});
const patientKey = ref("-");
const archiveReasonDialog = ref(false);
const intakeUI = ref(null);
const sendMethod = ref("fax"); // 'fax' or 'email'

// Email feature flag from client config (camelized by API interceptor)
const sendEmailEnabled = computed(() => requirementsStore.clientConfig?.intake?.sendEmailEnabled ?? false);
const availableTags = computed(() => requirementsStore.clientConfig?.intake?.availableTags || []);

// Get referral source emails from the intake order form
const referralSourceEmails = computed(() => {
  const orderForm = documentInfo.value?.intakeOrderForm;
  if (!orderForm?.extraData?.referralSourceEmail) {
    return [];
  }
  return [orderForm.extraData.referralSourceEmail];
});

// Generate email subject from patient info
const emailSubject = computed(() => {
  const patient = patientInfo.value;
  if (!patient) return "Document";
  const patientName = `${patient.firstName || ""} ${patient.lastName || ""}`.trim();
  return patientName ? `Document - ${patientName}` : "Document";
});

const emailSuggestedFiles = computed(() => {
  const url = documentInfo.value?.inputPresignedUrl;
  if (!url) return [];
  const patientName = `${patientInfo.value?.firstName || ""} ${patientInfo.value?.lastName || ""}`.trim();
  const fileName = patientName ? `${patientName} - Intake Document.pdf` : "Intake Document.pdf";
  return [{ name: fileName, link: url }];
});

// Build template context for email template variable substitution
const emailTemplateContext = computed(() => {
  const orderForm = documentInfo.value?.intakeOrderForm;
  const insuranceCards = documentInfo.value?.intakeInsuranceCards || [];
  const toDos = documentInfo.value?.intakeToDos || [];
  const patient = patientInfo.value;
  const raw = (orderForm?.extraData?.centerRep || "").trim();
  const isPlaceholder = /^(N\/A|n\/a|-|—|–)$/i.test(raw);
  const rs = isPlaceholder ? "" : raw;
  const centerRepFirstName = rs ? rs.split(/\s+/)[0] : "";

  return {
    patient: {
      firstName: patient?.firstName || "",
      lastName: patient?.lastName || "",
      middleName: patient?.middleName || "",
      dob: patient?.dobStr ? convertDateToMMDDYYYY(patient.dobStr) : "",
      phoneNumber: patient?.phoneNumber || "",
      address: patient?.address || "",
      externalId: patient?.externalId || "",
    },
    orderForm: {
      providerFirstName: orderForm?.providerFirstName || "",
      providerLastName: orderForm?.providerLastName || "",
      providerNpi: orderForm?.providerNpi || "",
      patientAddress: orderForm?.patientAddress || "",
      patientCity: orderForm?.patientCity || "",
      patientState: orderForm?.patientState || "",
      patientZip: orderForm?.patientZip || "",
      patientHomePhone: orderForm?.patientHomePhone || "",
      patientGender: orderForm?.patientGender || "",
      ...(orderForm?.extraData || {}),
      centerRepFirstName: centerRepFirstName,
    },
    insuranceCard:
      insuranceCards.length > 0
        ? {
            payorName: insuranceCards[0]?.payorName || "",
            memberId: insuranceCards[0]?.memberId || "",
            memberName: insuranceCards[0]?.memberName || "",
            insurancePlan: insuranceCards[0]?.insurancePlan || "",
            rxBin: insuranceCards[0]?.rxBin || "",
            rxPcn: insuranceCards[0]?.rxPcn || "",
            rxGrp: insuranceCards[0]?.rxGrp || "",
          }
        : {},
    toDo:
      toDos.length > 0
        ? {
            toDo: toDos[0]?.toDo || "",
            notes: toDos[0]?.notes || "",
            dueDate: toDos[0]?.dueDate || "",
            assignedTo: toDos[0]?.assignedTo || [],
          }
        : {},
  };
});

const updatePatientKey = () => {
  patientKey.value = `${patientInfo.value?.id}.${patientInfo.value?.modifiedOn}`;
};

const syncPatientAfterSave = () => {
  updatePatientKey();
  if (documentInfo.value && patientInfo.value) {
    documentInfo.value.patient = patientInfo.value;
  }
};

watch(
  [documentInfo],
  ([newInfo]) => {
    showLoader.value = !newInfo;
  },
  { immediate: true }
);

// Sync patientInfo back to the store so other components can react to patient changes
watch(
  patientInfo,
  (newPatient) => {
    if (intakeStore.intakeDocument && newPatient) {
      intakeStore.intakeDocument.patient = newPatient;
    }
  },
  { deep: true }
);

onMounted(async () => {
  intakeStore.reset();
  intakeStore.stage = INTAKE_STAGE.INTAKE;
  setIntakeSaveErrorHandler(demoteAutosaveErrorHandler);
  if (!(await acquireLockOrEnterViewer())) return;
  fetchIntakeDocument()
    .then(async () => {
      if (documentInfo.value) {
        await requirementsStore.refreshClientConfig(documentInfo.value.clientCompany);
        intakeUI.value = await getClientIntakeUI(
          requirementsStore.clientConfig,
          {},
          intakeStore.stage,
          intakeStore.pageManager
        );
        const patient = patientInfo.value ?? documentInfo.value?.patient;
        if (patient) {
          await intakeUI.value?.onPatientSelected(patient, {
            documentInfo: documentInfo.value,
            intakeService,
            onAttributeEdit,
            fetchIntakeDocument,
            pages: pageManager.pages,
          });
        }
      }
    })
    .catch((e) => {
      errorMsg.value = `Error loading the intake configuration: ${e.message ?? e}`;
    })
    .finally(() => {
      clientConfigLoading.value = false;
    });
  fetchIntakeDocumentPages();
  startPollInterval();
  window.addEventListener("beforeunload", onBeforeUnload);
});

const enterViewerMode = (lock) => {
  holdsLock.value = false;
  intakeStore.lockViewer = true;
  lockFree.value = false;
  lockHolder.value = lock?.user || null;
};

const acquireLockOrEnterViewer = async () => {
  if (isReadOnlyDevice.value) {
    await peekLockHolder(documentKey);
    return true;
  }
  try {
    await intakeService.acquireIntakeDocumentLock(documentKey);
  } catch (e) {
    if (e.response?.status === 423) {
      enterViewerMode(e.response.data?.lock);
      return true;
    }
    errorMsg.value = "Could not open the intake document.";
    router.push(listReturnTarget({ name: "all-patients", query: filtersStore.getNavigationQuery() }));
    return false;
  }
  holdsLock.value = true;
  startStaffTime(documentKey);
  wasEditor.value = true;
  return true;
};

onUnmounted(() => {
  clearIntakeSaveErrorHandler(demoteAutosaveErrorHandler);
  requirementsStore.refreshCurrentUserClientConfig();
});

const releaseLock = async () => {
  const documentKey = route.params.id;
  holdsLock.value = false;
  try {
    window.removeEventListener("beforeunload", onBeforeUnload);
    await intakeService.releaseIntakeDocumentLock(documentKey);
  } catch (e) {
    // Suppress
  }
};

onBeforeRouteLeave(async () => {
  stopPollInterval();
  if (!holdsLock.value) {
    window.removeEventListener("beforeunload", onBeforeUnload);
    if (wasEditor.value) await updateStaffTime();
    return;
  }
  await releaseLock();
  await updateStaffTime();
});

const onBeforeUnload = () => {
  stopPollInterval();
  if (!holdsLock.value) {
    if (wasEditor.value) updateStaffTime();
    return;
  }
  // Fire and forget the save updates
  patientInfo.value?.saveUpdates?.();
  documentInfo.value?.saveUpdates();
  releaseLockOnUnload();
  updateStaffTime();
};

const releaseLockOnUnload = () => {
  holdsLock.value = false;
  intakeService.releaseIntakeDocumentLockOnUnload(route.params.id);
};

const startStaffTime = (documentKey) => {
  try {
    intakeService.startIntakeDocumentStaffTime(documentKey);
  } catch (e) {
    // Suppress
  }
};

const updateStaffTime = async () => {
  const documentKey = route.params.id;
  try {
    await intakeService.pushIntakeDocumentStaffTime(documentKey);
  } catch (e) {
    // Suppress
  }
};

const renewLock = async () => {
  try {
    await intakeService.acquireIntakeDocumentLock(documentKey);
  } catch (e) {
    // A 423 renew means the lock lapsed and someone else took over; other errors are transient.
    if (e.response?.status === 423) {
      enterViewerMode(e.response.data?.lock);
      errorMsg.value = "This record is now being edited by another user.";
    }
  }
};

useIntervalWhenVisible(() => {
  if (!holdsLock.value) return;
  renewLock();
}, 60000);

// One 15-s tick carries both jobs: log refresh for everyone, lock peek for anyone not editing.
const startPollInterval = () => {
  if (pollInterval) return;
  pollInterval = setInterval(onPollTick, 15000);
};

const stopPollInterval = () => {
  clearInterval(pollInterval);
  pollInterval = null;
};

const peekLockHolder = async (key) => {
  let lock;
  try {
    lock = await intakeService.getIntakeDocumentLock(key);
  } catch (e) {
    // Transient peek failure — try again next tick
    return;
  }
  // A peek that resolves after the page left this document would publish the former
  // holder into the store the next document reads
  if (route.params.id !== key) return;
  if (lock) {
    enterViewerMode(lock);
  } else if (isReadOnlyDevice.value) {
    intakeStore.lockViewer = false;
    lockFree.value = false;
    lockHolder.value = null;
  } else {
    lockFree.value = true;
  }
};

const onPollTick = async () => {
  if (document.hidden) return;
  if (intakeStore.lockViewer || isReadOnlyDevice.value) await peekLockHolder(documentKey);
  auditLogListRef.value?.refresh();
};

const takeOverEditing = async () => {
  try {
    await intakeService.acquireIntakeDocumentLock(documentKey);
  } catch (e) {
    // Lost the takeover race — stay a viewer with the new holder's name.
    if (e.response?.status === 423) enterViewerMode(e.response.data?.lock);
    else errorMsg.value = "Could not take over editing — try again.";
    return;
  }
  intakeStore.lockViewer = false;
  lockFree.value = false;
  lockHolder.value = null;
  errorMsg.value = "";
  holdsLock.value = true;
  if (!wasEditor.value) startStaffTime(documentKey);
  wasEditor.value = true;
  showLoader.value = true;
  await Promise.all([fetchIntakeDocument(), fetchIntakeDocumentPages()]);
  showLoader.value = false;
};

const onPatientGuessHighlight = (data) => {
  previewRef.value?.onHighlightBoundingBox(data, documentInfo.value?.intakeOrderForm);
};

const onPatientAttributeChange = (name, value) => {
  patientInfo.value?.updateAttribute(name, value);
  patientInfo.value?.debouncedSaveUpdates();
};

const onSetPatient = async (patient) => {
  try {
    errorMsg.value = "";
    if (!patient) {
      await intakeService.patchIntakeDocument(documentKey, { patientId: null });
      patientInfo.value = null;
      additionalPatients.value = [];
      await intakeUI.value?.onPatientSelected(null, {});
      return;
    }
    const response = await intakeService.patchIntakeDocument(documentKey, { patientId: patient.id });
    patientInfo.value = new Patient(response.patient, dateAdapter);

    // Call client-specific hook for patient selection
    await intakeUI.value?.onPatientSelected(patient, {
      documentInfo: documentInfo.value,
      intakeService,
      onAttributeEdit,
      fetchIntakeDocument,
      pages: pageManager.pages,
    });
  } catch (e) {
    errorMsg.value = "Error saving patient to document";
  }
};

const onSetAdditionalPatients = async (patients) => {
  try {
    errorMsg.value = "";
    const patientIds = patients.map((p) => p.id);
    await intakeService.patchIntakeDocument(documentKey, { additionalPatientIds: patientIds });
    additionalPatients.value = patients.map((p) => (p instanceof Patient ? p : new Patient(p, dateAdapter)));
  } catch (e) {
    errorMsg.value = "Error saving additional patients";
  }
};

const onConfirmPatientSelection = async (primary, additional) => {
  const previousPrimary = patientInfo.value;
  const previousAdditional = [...additionalPatients.value];
  confirmSelectionLoading.value = true;
  errorMsg.value = "";
  try {
    await onSetPatient(primary);
    await onSetAdditionalPatients(additional ?? []);
  } catch {
    await onSetPatient(previousPrimary ?? null);
    await onSetAdditionalPatients(previousAdditional);
    // errorMsg already set by onSetPatient or onSetAdditionalPatients
  } finally {
    confirmSelectionLoading.value = false;
  }
};

const onUpdatePatient = async (patientId, fields) => {
  try {
    errorMsg.value = "";
    const response = await patientService.patchPatient(patientId, fields);
    const updatedPatient = new Patient(response, dateAdapter);

    if (patientId === patientInfo.value?.id) {
      patientInfo.value = new Patient(response, dateAdapter);
    } else {
      const idx = additionalPatients.value.findIndex((p) => p.id === patientId);
      if (idx >= 0) {
        additionalPatients.value[idx] = updatedPatient;
        additionalPatients.value = [...additionalPatients.value];
      }
    }
  } catch (e) {
    errorMsg.value = `Error updating patient: ${e.message}`;
  }
};

const onSaveNewPatient = async (newPatient) => {
  try {
    newPatient.clientCompany = documentInfo.value.clientCompany;
    const savedPatient = await patientService.postPatient(newPatient);
    patientInfo.value = new Patient(savedPatient, dateAdapter);
    await onSetPatient(savedPatient);
  } catch (e) {
    errorMsg.value = `Error saving the new patient: ${e.message}`;
  }
};

const submitStep = (payload) => {
  onclickIntakeSubmit(payload);
};

const backStep = async () => {
  router.push(listReturnTarget({ name: "all-patients", query: filtersStore.getNavigationQuery() }));
};

const downloadDocument = async () => {
  window.open(documentInfo.value.inputPresignedUrl, "_blank");
};

const onAttributeEdit = (field, value, type, id) => {
  if (type === "PATIENT" && patientInfo.value) {
    patientInfo.value.updateAttribute(field.key, value);
    patientInfo.value.debouncedSaveUpdates();
    return;
  }
  documentInfo.value.updateAttribute(field.key, value, type, id);
  documentInfo.value.debouncedSaveUpdates();
};

const demoteIfLockRejected = (e) => {
  if (e?.response?.status !== 423) return false;
  enterViewerMode(e.response.data?.lock);
  errorMsg.value = "This record is now being edited by another user.";
  return true;
};

// Autosaves reject asynchronously, so a rejection can arrive after navigation; only a lock
// rejection for the document this page owns may demote it — anything else (stale page,
// non-423 failure) rethrows as an unhandled save error.
const demoteAutosaveErrorHandler = (e) => {
  if (route.params.id !== documentKey) throw e;
  if (!demoteIfLockRejected(e)) throw e;
};

const errorWrapper = async (fn, errorPrefix) => {
  showLoader.value = true;
  let success = false;
  try {
    await fn();
    success = true;
  } catch (e) {
    if (!demoteIfLockRejected(e)) {
      errorMsg.value = `${errorPrefix} ${e.message}`;
    }
  } finally {
    showLoader.value = false;
  }
  return success;
};

const onAddContent = async (page, documentType, initialData = null) => {
  if (!(await savePageContent())) {
    return;
  }
  await errorWrapper(async () => {
    await intakeActions.onAddContent(page, documentType);
    await fetchIntakeDocument();
    if (initialData) {
      const contentMap = {
        INSURANCE_CARD: documentInfo.value?.intakeInsuranceCards,
        ORDER_FORM: documentInfo.value?.intakeOrderForm ? [documentInfo.value.intakeOrderForm] : [],
        TO_DO: documentInfo.value?.intakeToDos,
      };
      const items = contentMap[documentType] || [];
      // Sort by createdOn descending to find the most recently created item
      const newestItem = [...items].sort((a, b) => (b.createdOn || 0) - (a.createdOn || 0))[0];
      if (newestItem) {
        Object.entries(initialData).forEach(([key, value]) => {
          newestItem.updateAttribute(key, value);
        });
        await newestItem.saveUpdates();
      }
    }
  }, "Error adding the content");
};

const onDeleteContent = async (content, documentType) => {
  if (!(await intakeActions.confirmDeleteContent(documentType))) {
    return;
  }
  if (!(await savePageContent())) {
    return;
  }
  await errorWrapper(async () => {
    await intakeActions.onDeleteContentConfirm(content, documentType);
    await fetchIntakeDocument();
  }, "Error deleting the content");
};

const savePageContent = async () => {
  return await errorWrapper(async () => {
    await documentInfo.value.saveUpdates();
  }, "Error saving the page content");
};

const validateIntakeData = async () => {
  const formErrors = previewRef.value.formErrors;
  const isValid = await handleFormValidationErrors(formErrors);
  if (!isValid) {
    errorMsg.value = "There are some errors with the document data";
  }
  return isValid;
};

const onclickIntakeSubmit = async (payload) => {
  if (buttonsDisabled.value) {
    return;
  }

  try {
    errorMsg.value = "";
    buttonsDisabled.value = true;
    patientInfo.value?.debouncedSaveUpdates?.cancel?.();
    await patientInfo.value?.saveUpdates?.();
    syncPatientAfterSave();
    await Promise.all([
      documentInfo.value.saveUpdates(),
      ...(pageManager.pages?.map((page) => page.debouncedSaveUpdates.flush()) || []),
      intakeStore.pendingPageSave,
    ]);

    // Client-specific: allow submit without patient when e.g. all pages Spam (Brooks archive path)
    if (intakeUI.value?.shouldSubmitWithoutPatientWhenAllSpam(pageManager.pages)) {
      showLoader.value = true;
      await intakeService.postIntakeDocumentSubmit(route.params.id);
      router.push(listReturnTarget({ name: "all-patients", query: filtersStore.getNavigationQuery() }));
      return;
    }

    if (!patientInfo.value) {
      errorMsg.value = "Please select a patient before continuing";
      return;
    }
    if (pageManager.pages?.some((page) => page.documentType === "")) {
      errorMsg.value = "Please classify all document pages before continuing";
      return;
    }
    // Client-specific validation
    const clientValidationError = intakeUI.value?.validateIntakeSubmit(
      patientInfo.value,
      documentInfo.value.intakeOrderForm,
      documentInfo.value.intakeToDos
    );
    if (clientValidationError) {
      errorMsg.value = clientValidationError;
      return;
    }
    if (!(await validateIntakeData())) {
      return;
    }
    showLoader.value = true;
    const order = await intakeService.postIntakeDocumentSubmit(route.params.id, payload);
    // If client configuration has inboxOnly flag, always route back to the inbox
    const inboxOnly = requirementsStore.clientConfig?.intake?.inboxOnly;
    if (inboxOnly) {
      router.push(listReturnTarget({ name: "all-patients", query: filtersStore.getNavigationQuery() }));
      return;
    }
    if (order) {
      if (order.patient?.hasInsuranceCard) {
        router.push({ name: "qualify-order", params: { id: order.id } });
      } else {
        router.push(listReturnTarget({ name: "all-patients", query: filtersStore.getNavigationQuery() }));
      }
    } else {
      router.push(listReturnTarget({ name: "all-patients", query: filtersStore.getNavigationQuery() }));
    }
  } catch (ex) {
    if (!demoteIfLockRejected(ex)) {
      errorMsg.value = `Error submitting the document: ${ex.message}`;
    }
  } finally {
    showLoader.value = false;
    buttonsDisabled.value = false;
  }
};

const fetchIntakeDocumentPages = async () => {
  pagesLoading.value = true;
  try {
    const documentKey = route.params.id;
    const response = await intakeService.getIntakeDocumentPages(documentKey);
    const pages = [];
    response.results.forEach((pageData) => {
      const page = new IntakeDocumentPage(pageData, dateAdapter);
      pages.push(page);
    });
    allPages.value = pages;
    pageManager.pages = pages;
    currentDocument.value = pages[0];
    reviewContainerHeight.value = "unset";
  } catch (e) {
    errorMsg.value = `Error fetching the document pages: ${e.message ?? e}`;
  } finally {
    pagesLoading.value = false;
  }
};

const fetchIntakeDocument = async () => {
  const documentKey = route.params.id;
  try {
    const response = await intakeService.getIntakeDocument(documentKey);
    intakeStore.intakeDocument = new IntakeDocument(response, dateAdapter);
    const patient = response.patient;
    if (patient) {
      patientInfo.value = new Patient(patient, dateAdapter);
    } else {
      patientInfo.value = null;
    }
    additionalPatients.value = (response.additionalPatients || []).map((p) => new Patient(p, dateAdapter));
  } catch (e) {
    errorMsg.value = `Error fetching the document: ${e.message}`;
  }
};

const onSnooze = async ({ minutes }) => {
  if (intakeStore.lockViewer) return;
  await errorWrapper(async () => {
    intakeStore.intakeDocument = await intakeActions.onSnooze({ documentKey, minutes });
  }, "Error snoozing the document");
};

const onArchive = async () => {
  if (intakeStore.lockViewer) return;
  if (documentInfo.value.status === DOCUMENT_STAGE.ARCHIVED) {
    try {
      await intakeService.deleteArchiveDocument(documentKey);
      intakeStore.setReadOnly(false);
      return;
    } catch (e) {
      errorMsg.value = `Error unarchiving the document: ${e.message}`;
    }
  }
  archiveReasonDialog.value = true;
};
const onArchived = () => {
  archiveReasonDialog.value = false;
  router.push(listReturnTarget({ name: "all-patients", query: filtersStore.getNavigationQuery() }));
};

const submitActions = computed(() => {
  return (
    intakeUI.value?.getInboxSubmitActions({
      orderForm: documentInfo.value.intakeOrderForm,
      patient: patientInfo.value,
    }) ?? []
  );
});

const allowNewPatientRegistration = computed(() => {
  return intakeUI.value?.shouldAllowNewPatientRegistration() ?? true;
});

const intakeWarnings = computed(() => {
  if (!intakeUI.value) return [];
  return intakeUI.value.getIntakeWarnings({
    patient: patientInfo.value,
    pages: pageManager.pages,
  });
});

const onWarningNote = (warning) => {
  errorMsg.value = `Warning: ${replaceTableMarkers(warning.userNote?.note)}`;
};

const onMentionsSent = (assigned) => {
  if (!documentInfo.value) return;
  const current = documentInfo.value.assignees || [];
  const ids = new Set(current.map((a) => a.id));
  documentInfo.value.assignees = [...current, ...assigned.filter((a) => !ids.has(a.id))];
};
</script>

<style lang="scss" scoped>
@import "@/styles/pages/_patient-intake-review.scss";
</style>
